import { If, onCleanup, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { routeReplace } from '../../../lib/browser';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { BankCardView } from './Card';
import { BankShell } from './Shell';
import {
  fetchMarketBanks,
  fetchPendingBankApplies,
  listUrl,
  refreshCardNames,
  setMarketBankCache,
  toPendingBankCard,
  unwrapList,
  type BankCard,
} from './shared';
import './bank.css';

/**
 * 添加列表 → 银行账户：固定 replace，不走 history.back。
 * WebView 里 history.length 常 >1 但上一页不是银行列表；history.back 会直接出栈关页。
 */
const backToBankList = () => {
  const target = listUrl();
  console.log('[bank-pending] backToBankList', {
    href: typeof window !== 'undefined' ? window.location.href : '',
    pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    search: typeof window !== 'undefined' ? window.location.search : '',
    historyLength: typeof window !== 'undefined' ? window.history.length : -1,
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    historyState: typeof window !== 'undefined' ? window.history.state : null,
    hasRN: typeof window !== 'undefined' && !!(window as any).ReactNativeWebView,
    target,
  });
  // 下一 macrotask 再跳：避免同步挂载 List Shell 时仍处在同一次 GoBack 分发中
  window.setTimeout(() => routeReplace(target), 0);
};

const BankCardSkeleton = () => (
  <div class="bank-card bank-card-skeleton difi-skeleton-animate" aria-busy="true">
    <div class="bank-card-left">
      <div class="bank-card-top">
        <div class="bank-skel-logo" />
        <div class="bank-card-info">
          <div class="bank-skel-line bank-skel-name" />
          <div class="bank-skel-line bank-skel-no" />
        </div>
      </div>
      <div class="bank-skel-line bank-skel-currency" />
    </div>
    <div class="bank-skel-arrow" />
  </div>
);

export const BankAccountPendingList = () => {
  const state = reactive({
    loading: true,
    rows: [] as BankCard[],
  });

  let reqId = 0;

  const load = async (silent: boolean) => {
    const id = ++reqId;
    if (!silent) state.loading = true;
    try {
      const rows = unwrapList(await fetchPendingBankApplies()).map((item, index) => toPendingBankCard(item, index));
      if (id !== reqId) return;
      state.rows = rows;
    } catch {
      if (id !== reqId) return;
      if (!silent) state.rows = [];
    } finally {
      if (id === reqId) state.loading = false;
    }
  };

  const loadNames = async () => {
    try {
      setMarketBankCache(unwrapList(await fetchMarketBanks()));
      state.rows = refreshCardNames(state.rows);
    } catch {
      // 没有字典就先显示接口名
    }
  };

  onMount(() => {
    void bootDepositApp()
      .catch(() => {})
      .then(() => {
        void load(false);
        void loadNames();
      });

    const onShow = () => {
      if (document.visibilityState !== 'visible' || state.loading) return;
      void load(true);
    };
    document.addEventListener('visibilitychange', onShow);
    onCleanup(() => document.removeEventListener('visibilitychange', onShow));
  });

  return (
    <BankShell title={i18n.BankList.AddListTitle} backFn={backToBankList}>
      <div class="list">
        <If when={state.loading}>
          <div class="bank-list">
            <BankCardSkeleton />
            <BankCardSkeleton />
          </div>
        </If>
        <If when={!state.loading && !state.rows.length}>
          <div class="empty">{i18n.BankList.AddListEmpty}</div>
        </If>
        <If when={!state.loading && !!state.rows.length}>
          <div class="bank-list">
            {state.rows.map((item) => (
              <BankCardView item={item} showPending />
            ))}
          </div>
        </If>
      </div>
    </BankShell>
  );
};
